import TD, { TopDownFormProps } from "@/components/forms/TopDownForm";
import { useCallback, useState } from "react";
import {
  array,
  create,
  literal,
  never,
  optional,
  string,
  type,
  union,
} from "superstruct";
import { IConceptInputElement } from "./ConceptInputElement";
import { ICoding } from "@/data-models/value-models";
import FieldArray from "@/components/forms/FieldArray";
import { CodingModel } from "@/data-models/value-models/structs";
import { Combobox } from "@headlessui/react";
import MakeValueSetForm, {
  FormDialog,
} from "@/components/forms/MakeValueSetForm/MakeValueSetForm";
import ValueSetInputv2, {
  ValueSetMeta,
} from "@/components/controlled/ValueSetInput/ValueSetInputv2";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";
import CodingCardList from "@/components/base/CodingCard/CodingCardList";
import useCodeCombobox from "@/components/base/CodeCombobox/useCodeCombobox";
import { IHumanReadableCoding } from "@/data-models/value-models/types";
import classNames from "classnames";
import Button from "@/components/base/CodeCombobox/Button";
import { FloatingPortal } from "@floating-ui/react";
import CodeComboboxResults from "@/components/base/CodeCombobox/CodeComboboxResults";

type FieldValues = {
  inputType: IConceptInputElement["inputType"];
  valueSetVersion?: string | null;
  valueSet?: string | null;
  options?: ICoding[];
  placeholder?: string | null;
};

type SubmissionValues =
  | {
      options: ICoding[];
      placeholder: string;
      valueSet?: never;
      inputType: "dropdown";
    }
  | {
      options: ICoding[];
      placeholder?: never;
      valueSet?: never;
      inputType: "chips";
    }
  | {
      valueSet: string;
      placeholder: string;
      options?: never;
      inputType: "dropdown";
    }
  | {
      valueSet: string;
      placeholder?: never;
      options?: never;
      inputType: "chips";
    };
const SubmissionValuesModel = union([
  type({
    options: array(CodingModel),
    placeholder: string(),
    valueSet: optional(never()),
    inputType: literal("dropdown"),
  }),
  type({
    options: array(CodingModel),
    placeholder: optional(never()),
    valueSet: optional(never()),
    inputType: literal("chips"),
  }),
  type({
    options: optional(never()),
    placeholder: string(),
    valueSet: string(),
    inputType: literal("dropdown"),
  }),
  type({
    options: optional(never()),
    placeholder: optional(never()),
    valueSet: string(),
    inputType: literal("chips"),
  }),
]);

interface PropertiesFormProps
  extends Omit<TopDownFormProps<FieldValues>, "onSubmit"> {
  actionLabel: string;
  optionValueSet?: string;
  onSubmit?: (values: SubmissionValues) => void;
}

const DEFAULT_VALUESET =
  "http://tiro.health/fhir/ValueSet/snomed-ct-auto-generated";
function PropertiesForm({
  defaultValues,
  optionValueSet = DEFAULT_VALUESET,
  onSubmit,
  actionLabel,
}: PropertiesFormProps) {
  const { t } = useTranslation();
  const [showMakeValueSetDialog, setShowMakeValueSetDialog] = useState(false);
  const methods = useForm<FieldValues>({ defaultValues });
  const {
    register,
    unregister,
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
  } = methods;
  const updateValues = useCallback(
    (raw: FieldValues) => {
      const values = {
        ...raw,
        placeholder: raw.inputType == "dropdown" ? raw.placeholder : undefined,
      };
      return onSubmit && onSubmit(create(values, SubmissionValuesModel));
    },
    [onSubmit]
  );

  const switchMode = (next: "valueSet" | "options") => {
    if (next == "options") {
      unregister("valueSet");
      unregister("valueSetVersion");
      setValue("options", []);
    } else {
      unregister("options");
      setValue("options", undefined);
      setValue("valueSet", null);
      setValue("placeholder", null);
    }
  };

  return (
    <div>
      {Object.keys(errors).length > 0 && (
        <p className="prose">
          <pre>{JSON.stringify(errors, null, 2)}</pre>
        </p>
      )}
      <form onSubmit={handleSubmit(updateValues)}>
        <FormProvider {...methods}>
          <fieldset className="mt-2 block pb-2">
            <TD.Field
              {...register("inputType")}
              InputComponent={TD.Select as any}
              defaultValue="dropdown"
              label="Input type"
              small
            >
              <option value="chips">Chips</option>
              <option value="dropdown">Dropdown</option>
            </TD.Field>
          </fieldset>
          {!watch("options") && (
            <fieldset className="mt-2 border-b border-gray-lighter pb-2">
              <div className="justify relative flex justify-between">
                <legend className="text-base font-medium text-gray-900">
                  Opties uit ValueSet
                </legend>
                <button
                  type="button"
                  onClick={() => switchMode("options")}
                  className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                >
                  {t("use-options")}
                </button>
              </div>
              <ValueSetInputv2 name="valueSet">
                {({ query, onQueryChange, valueSet }) => {
                  return (
                    <>
                      <ValueSetInputv2.Label className="sr-only">
                        Zoek ValueSet
                      </ValueSetInputv2.Label>
                      <ValueSetInputv2.Wrapper className="mt-1" query={query}>
                        <ValueSetInputv2.Input
                          autoComplete="off"
                          as={TD.Input}
                          onChange={(event) =>
                            onQueryChange(event.target.value)
                          }
                          displayValue={(vs: ValueSetMeta | null) =>
                            vs?.title ?? ""
                          }
                        />
                      </ValueSetInputv2.Wrapper>
                      <ValueSetInputv2.VersionSelect
                        name="valueSetVersion"
                        url={valueSet?.url ?? ""}
                      />
                      {watch("inputType") == "dropdown" && (
                        <TD.Field
                          small
                          label="Placeholder"
                          {...register("placeholder", {
                            value: valueSet?.title,
                            deps: ["valueSet"],
                          })}
                        />
                      )}
                    </>
                  );
                }}
              </ValueSetInputv2>
            </fieldset>
          )}
          {watch("options") && (
            <fieldset className="mt-2 border-b border-gray-lighter pb-2">
              <div className="justify relative flex justify-between">
                <legend className="text-base font-medium text-gray-900">
                  Opties codelijst
                </legend>
                <button
                  type="button"
                  onClick={() => switchMode("valueSet")}
                  className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                >
                  {t("use-valueset")}
                </button>
              </div>
              {/** ADd Make valuests */}
              <FieldArray<FieldValues> name="options">
                {({ fields, remove, append }) => (
                  <>
                    <CodingCardList codes={fields} onCodeRemove={remove} />
                    <CodingComboboxInput
                      onCodeChange={(code) => code && append(code)}
                      valueSet={optionValueSet}
                    />
                  </>
                )}
              </FieldArray>
              {watch("inputType") == "dropdown" && (
                <TD.Field
                  type="text"
                  {...register("placeholder", { required: true })}
                  label="Placeholder"
                  small
                />
              )}
            </fieldset>
          )}
          <TD.Submit small>{actionLabel}</TD.Submit>
        </FormProvider>
      </form>
      <FormDialog
        isOpen={showMakeValueSetDialog}
        close={() => setShowMakeValueSetDialog(false)}
      >
        <MakeValueSetForm
          defaultValues={{ options: watch("options") }}
          close={() => setShowMakeValueSetDialog(false)}
        />
      </FormDialog>
    </div>
  );
}

export default PropertiesForm;

type CodingComboboxProps = {
  className?: string;
  code?: IHumanReadableCoding | null;
  onCodeChange?: (code: IHumanReadableCoding | null) => void;
  valueSet: string;
};

function CodingComboboxInput({
  valueSet,
  code = null,
  onCodeChange,
  className,
  ...props
}: CodingComboboxProps) {
  const {
    query,
    filteredCodes,
    handleQueryChange,
    by,
    displayValue,
    handleCodeChange,
    referenceProps,
    floatingProps,
    fetchStatus,
    helpers,
  } = useCodeCombobox(valueSet, {
    allowedPlacements: ["bottom-start", "bottom-end"],
  });

  return (
    <Combobox
      as="div"
      className={classNames(
        "inline-flex max-w-md items-baseline justify-between",
        className
      )}
      value={code}
      onChange={handleCodeChange(onCodeChange)}
      by={by}
      {...props}
    >
      {({ open }) => (
        <div
          {...referenceProps}
          className={classNames("relative grow rounded-md shadow-sm")}
        >
          <Combobox.Input
            autoComplete="off"
            className="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-12 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 sm:text-sm sm:leading-6"
            value={query}
            onChange={handleQueryChange}
            displayValue={displayValue}
          />
          <Combobox.Button
            as={Button}
            loading={fetchStatus.isFetching}
            error={fetchStatus.isError}
            success={fetchStatus.isSuccess}
          />
          <FloatingPortal>
            <CodeComboboxResults
              {...floatingProps}
              open={open && query.trim().length > 0}
              minimal
              codes={filteredCodes}
              helpers={helpers}
            />
          </FloatingPortal>
        </div>
      )}
    </Combobox>
  );
}
